import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "./styles.css";
import Container from "../../../components/Container";
import { Input, Table, Button, Tag, Modal, notification, Spin } from "antd";
import { db } from "../../../services/firebase";
import { collection, getDocs, deleteDoc, doc, query, where } from "firebase/firestore";
import { EyeOutlined, EditOutlined, DeleteOutlined, TeamOutlined, ExclamationCircleOutlined, PlusOutlined, SafetyCertificateOutlined } from "@ant-design/icons";

const { Search } = Input;

function ClientList() {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  const fetchClientsFirebase = useCallback(async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(collection(db, "clientes"));
      const clientesData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      // Buscar contagem de certificados para cada cliente
      const certificadosSnapshot = await getDocs(collection(db, "certificados"));
      const certificadosPorCliente = {};

      certificadosSnapshot.docs.forEach((doc) => {
        const clienteId = doc.data().cliente_id;
        if (clienteId) {
          certificadosPorCliente[clienteId] = (certificadosPorCliente[clienteId] || 0) + 1;
        }
      });

      // Adicionar contagem de certificados aos clientes
      const data = clientesData.map((cliente) => ({
        ...cliente,
        certificadosCount: certificadosPorCliente[cliente.id] || 0,
      }));

      // Ordenar por data de criação (mais novos primeiro)
      const orderedData = data.sort((a, b) => {
        const dateA = a.dataCadastro?.toDate?.() || a.dataCadastro?.seconds ? new Date(a.dataCadastro.seconds * 1000) : new Date(0);
        const dateB = b.dataCadastro?.toDate?.() || b.dataCadastro?.seconds ? new Date(b.dataCadastro.seconds * 1000) : new Date(0);
        return dateB - dateA;
      });

      setFilteredClients(orderedData);
      setClients(orderedData);
    } catch (error) {
      console.error("Erro ao buscar clientes:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Função para contar itens vinculados ao cliente
  const countLinkedItems = async (clienteId) => {
    const [psvSnapshot, controleSnapshot, bloqueioSnapshot, usuariosSnapshot, certificadosSnapshot] = await Promise.all([
      getDocs(query(collection(db, "valvulasPSV"), where("cliente_id", "==", clienteId))),
      getDocs(query(collection(db, "valvulasControle"), where("cliente_id", "==", clienteId))),
      getDocs(query(collection(db, "valvulasBloqueio"), where("cliente_id", "==", clienteId))),
      getDocs(query(collection(db, "usuarios"), where("cliente_id", "==", clienteId))),
      getDocs(query(collection(db, "certificados"), where("cliente_id", "==", clienteId))),
    ]);

    return {
      valvulas: psvSnapshot.size + controleSnapshot.size + bloqueioSnapshot.size,
      usuarios: usuariosSnapshot.size,
      certificados: certificadosSnapshot.size,
      psvDocs: psvSnapshot.docs,
      controleDocs: controleSnapshot.docs,
      bloqueioDocs: bloqueioSnapshot.docs,
      usuariosDocs: usuariosSnapshot.docs,
      certificadosDocs: certificadosSnapshot.docs,
    };
  };

  // Função para excluir todos os itens vinculados
  const deleteLinkedItems = async (linkedItems) => {
    const deletePromises = [];

    // Excluir válvulas PSV
    linkedItems.psvDocs.forEach((docSnap) => {
      deletePromises.push(deleteDoc(doc(db, "valvulasPSV", docSnap.id)));
    });

    // Excluir válvulas Controle
    linkedItems.controleDocs.forEach((docSnap) => {
      deletePromises.push(deleteDoc(doc(db, "valvulasControle", docSnap.id)));
    });

    // Excluir válvulas Bloqueio
    linkedItems.bloqueioDocs.forEach((docSnap) => {
      deletePromises.push(deleteDoc(doc(db, "valvulasBloqueio", docSnap.id)));
    });

    // Excluir usuários
    linkedItems.usuariosDocs.forEach((docSnap) => {
      deletePromises.push(deleteDoc(doc(db, "usuarios", docSnap.id)));
    });

    // Excluir certificados
    linkedItems.certificadosDocs.forEach((docSnap) => {
      deletePromises.push(deleteDoc(doc(db, "certificados", docSnap.id)));
    });

    await Promise.all(deletePromises);
  };

  const handleDelete = async (id, nomeEmpresa) => {
    // Primeiro, buscar quantos itens estão vinculados
    const linkedItems = await countLinkedItems(id);
    const hasLinkedItems = linkedItems.valvulas > 0 || linkedItems.usuarios > 0 || linkedItems.certificados > 0;

    let contentMessage = `O cliente "${nomeEmpresa}" será removido permanentemente.`;

    if (hasLinkedItems) {
      const items = [];
      if (linkedItems.valvulas > 0) items.push(`${linkedItems.valvulas} válvula(s)`);
      if (linkedItems.usuarios > 0) items.push(`${linkedItems.usuarios} usuário(s)`);
      if (linkedItems.certificados > 0) items.push(`${linkedItems.certificados} certificado(s)`);

      contentMessage = (
        <div>
          <p style={{ marginBottom: 12 }}>
            <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
            <strong>Atenção!</strong> Este cliente possui itens vinculados:
          </p>
          <ul style={{ marginBottom: 12, paddingLeft: 20 }}>
            {linkedItems.valvulas > 0 && <li>{linkedItems.valvulas} válvula(s)</li>}
            {linkedItems.usuarios > 0 && <li>{linkedItems.usuarios} usuário(s)</li>}
            {linkedItems.certificados > 0 && <li>{linkedItems.certificados} certificado(s)</li>}
          </ul>
          <p style={{ color: '#ff4d4f', fontWeight: 500 }}>
            Todos esses itens serão excluídos permanentemente junto com o cliente "{nomeEmpresa}".
          </p>
        </div>
      );
    }

    Modal.confirm({
      title: "Deseja excluir este cliente?",
      content: contentMessage,
      okText: "Sim, excluir tudo",
      okType: "danger",
      cancelText: "Cancelar",
      width: 500,
      onOk: async () => {
        try {
          // Excluir itens vinculados primeiro
          if (hasLinkedItems) {
            await deleteLinkedItems(linkedItems);
          }

          // Excluir o cliente
          await deleteDoc(doc(db, "clientes", id));

          notification.success({
            message: "Cliente excluído!",
            description: hasLinkedItems
              ? `O cliente e todos os ${linkedItems.valvulas + linkedItems.usuarios + linkedItems.certificados} itens vinculados foram removidos.`
              : "O cliente foi removido com sucesso.",
            placement: "topRight",
          });
          fetchClientsFirebase();
        } catch (error) {
          console.error("Erro ao excluir cliente:", error);
          notification.error({
            message: "Erro ao excluir",
            description: "Não foi possível excluir o cliente.",
            placement: "topRight",
          });
        }
      },
    });
  };

  const columns = [
    {
      title: "Nome",
      dataIndex: "nomeEmpresa",
      key: "nomeEmpresa",
    },
    {
      title: "CNPJ",
      dataIndex: "unidades",
      key: "cnpj",
      render: (unidades) => {
        if (!unidades || unidades.length === 0) return "-";
        return unidades[0]?.cnpj || "-";
      },
    },
    {
      title: "Unidades",
      dataIndex: "unidades",
      key: "unidades",
      render: (unidades) => {
        if (!unidades || unidades.length === 0) {
          return <span className="no-units">Nenhuma unidade</span>;
        }
        return (
          <div className="units-tags">
            {unidades.map((unidade, index) => (
              <Tag key={index} className="unit-tag">
                {unidade.unidade}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: "Certificados",
      dataIndex: "certificadosCount",
      key: "certificados",
      width: 120,
      align: "center",
      sorter: (a, b) => a.certificadosCount - b.certificadosCount,
      render: (count) => (
        <div className="certificados-count">
          <SafetyCertificateOutlined className={count > 0 ? "cert-icon-active" : "cert-icon-inactive"} />
          <span className={count > 0 ? "cert-count-active" : "cert-count-inactive"}>
            {count}
          </span>
        </div>
      ),
    },
    {
      title: "Ações",
      dataIndex: "id",
      key: "actions",
      width: 140,
      render: (id, record) => (
        <div className="actions-cell">
          <Link to={`/admin/cadastro-cliente/${id}`} className="action-btn view">
            <EyeOutlined />
          </Link>
          <Link to={`/admin/cadastro-cliente/${id}`} className="action-btn edit">
            <EditOutlined />
          </Link>
          <button
            onClick={() => handleDelete(id, record.nomeEmpresa)}
            className="action-btn delete"
          >
            <DeleteOutlined />
          </button>
        </div>
      ),
    },
  ];

  const filterClients = useCallback((search) => {
    const result = clients.filter(
      (client) =>
        client.nomeEmpresa.toLowerCase().includes(search.toLowerCase()) ||
        client.unidades.some((item) => item.cnpj.includes(search))
    );
    setFilteredClients(result);
  }, [clients]);

  const clearFilter = useCallback(() => {
    setFilteredClients(clients);
    searchRef.current.input.value = "";
  }, [clients]);

  useEffect(() => {
    fetchClientsFirebase();
  }, [fetchClientsFirebase]);

  return (
    <Container>
      <div className="client-list-container">
        <div className="list-card">
          <div className="list-header">
            <div className="list-header-left">
              <div className="list-icon">
                <TeamOutlined />
              </div>
              <div>
                <h2 className="list-title">Clientes Cadastrados</h2>
                <p className="list-subtitle">{filteredClients.length} clientes encontrados</p>
              </div>
            </div>
          </div>

          <div className="filters-bar">
            <div className="filter-input">
              <Search
                placeholder="Buscar por nome ou CNPJ"
                onSearch={filterClients}
                ref={searchRef}
                enterButton
              />
            </div>
            <Button onClick={clearFilter} className="filter-btn">
              Limpar
            </Button>
          </div>

          <Table
            dataSource={filteredClients}
            columns={columns}
            rowKey="id"
            loading={loading}
            className="custom-table"
          />
        </div>

        <Link to="/admin/cadastro-cliente" className="fab-button" title="Novo Cliente">
          <PlusOutlined />
        </Link>
      </div>
    </Container>
  );
}

export default ClientList;
